import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import { CATEGORIES_COLLECTION } from '@/modules/inventory/services/constants';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import type { Category, CategoryInput } from '@/modules/inventory/types';
import type { UserProfile } from '@/types/user';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type CategoryDoc = {
  companyId: string;
  name: string;
  description: string;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapCategory(id: string, data: CategoryDoc): Category {
  return {
    id,
    companyId: data.companyId,
    name: data.name,
    description: data.description,
    active: data.active,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listCategories(
  companyId: string = appConfig.companyId,
): Promise<Category[]> {
  return withFirestoreError(async () => {
    const q = query(
      collection(getFirestoreDb(), CATEGORIES_COLLECTION),
      where('companyId', '==', companyId),
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => mapCategory(d.id, d.data() as CategoryDoc))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, 'list');
}

export async function getCategory(id: string): Promise<Category | null> {
  const snap = await getDoc(doc(getFirestoreDb(), CATEGORIES_COLLECTION, id));
  if (!snap.exists()) return null;
  return mapCategory(snap.id, snap.data() as CategoryDoc);
}

export async function saveCategory(
  input: CategoryInput,
  id?: string,
  profile?: UserProfile | null,
): Promise<Category> {
  return withFirestoreError(async () => {
    const isNew = !id;
    const ref = isNew
      ? doc(collection(getFirestoreDb(), CATEGORIES_COLLECTION))
      : doc(getFirestoreDb(), CATEGORIES_COLLECTION, id);
    const existing = isNew ? null : await getDoc(ref);
    const before = existing?.exists()
      ? toAuditSnapshot(
          mapCategory(existing!.id, existing!.data() as CategoryDoc),
        )
      : undefined;
    const payload = {
      companyId: appConfig.companyId,
      name: input.name.trim(),
      description: input.description.trim(),
      active: input.active,
      ...buildTimestampPayload(isNew),
    };
    await setDoc(ref, payload, { merge: true });
    const saved = await getDoc(ref);
    if (!saved.exists()) {
      throw new Error('No se pudo guardar la categoría.');
    }
    const result = mapCategory(saved.id, saved.data() as CategoryDoc);
    recordEntityWrite({
      entityType: 'category',
      entityId: result.id,
      isCreate: isNew,
      ...(before !== undefined ? { before } : {}),
      after: toAuditSnapshot(result),
    });
    return result;
  }, 'category', profile);
}
